import { Link } from "react-router-dom";
import { AlertTriangle, ShieldAlert } from "lucide-react";
import { ClassificationBadge } from "./ClassificationBadge";
import { effectiveClassification } from "@/lib/format";
import { useConvertedPrice } from "@/lib/currency";

export function ClothingItemCard({ item }) {
  const overall = effectiveClassification(item.materials);
  const convertedPrice = useConvertedPrice(item.price, item.currency);
  return (
    <Link
      to={`/clothing/${item.slug}`}
      className="text-left group bg-white border border-line rounded-2xl overflow-hidden hover:shadow-lg transition-shadow w-full block"
      data-testid={`clothing-card-${item.slug}`}
    >
      <div className="aspect-[3/4] bg-champagne/30 overflow-hidden relative">
        {item.image_url ? (
          <img src={item.image_url} alt={item.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-500" onError={(e) => { e.currentTarget.style.display = "none"; }} />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-muted_text text-xs">No image</div>
        )}
        <div className="absolute top-3 left-3 flex flex-col gap-2 items-start">
          {item.is_featured && (
            <span
              className="text-[10px] font-semibold uppercase tracking-wider rounded-full px-2 py-1 bg-leaf/15 text-leaf"
              data-testid="card-featured-pill"
            >
              Featured
            </span>
          )}
          {item.false_advertising_confirmed ? (
            <span
              title="The brand's stated material composition doesn't match the actual product (verified by our editors)."
              className="inline-flex items-center gap-1.5 bg-clay text-ivory text-xs font-medium uppercase tracking-wider px-2.5 py-1 rounded-full shadow-md"
              data-testid="card-materials-mislabeled"
            >
              <ShieldAlert className="w-3.5 h-3.5" />
              Materials mislabeled
            </span>
          ) : item.false_advertising_suspected ? (
            <span
              title="The brand's stated material composition may not match the actual product. Under editorial review."
              className="inline-flex items-center gap-1.5 bg-amber text-ivory text-xs font-medium uppercase tracking-wider px-2.5 py-1 rounded-full shadow-md"
              data-testid="card-materials-disputed"
            >
              <AlertTriangle className="w-3.5 h-3.5" />
              Materials disputed
            </span>
          ) : null}
        </div>
      </div>
      <div className="p-3.5">
        <div className="flex items-center justify-between gap-2 mb-1">
          <div className="text-[10px] uppercase tracking-wider text-muted_text truncate">{item.brand_name}</div>
          {overall && <ClassificationBadge classification={overall} className="text-[10px] px-2 py-0.5 shrink-0" testId={`card-overall-${overall.toLowerCase()}`} />}
        </div>
        <h3 className="font-serif text-base leading-tight mb-1 truncate" title={item.name}>{item.name}</h3>
        <div className="text-[11px] text-muted_text mb-2 truncate">{item.clothing_item_type_name}</div>
        <div className="flex flex-wrap gap-x-1 gap-y-1 mb-2">
          {(item.materials || []).slice(0, 4).map((m, i) => (
            <span
              key={`${m.material_id || m.material_name}-${i}`}
              className="inline-flex items-center rounded-full border border-line bg-ivory px-1.5 py-0.5 text-[9px] text-ink/80 leading-snug"
              data-testid={`material-chip-${i}`}
            >
              {m.material_name}
              {m.percentage != null ? ` ${m.percentage}%` : ""}
            </span>
          ))}
        </div>
        {item.price !== null && item.price !== undefined && convertedPrice && (
          <div className="flex items-center flex-wrap gap-2 text-sm">
            <span data-testid="card-price-converted">{convertedPrice}</span>
            {item.label_verified && (
              <span
                title="Brand-published label verified by editors"
                className="text-xs text-leaf"
                data-testid="card-label-verified"
              >
                ✓ Label verified
              </span>
            )}
          </div>
        )}
      </div>
    </Link>
  );
}
